import React, { useState, useMemo } from "react";
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Switch } from "react-native";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";

// Simple Dropdown Component (Replace with a proper one if available)
const SimpleDropdown = ({ label, options, selectedValue, onSelect }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const selectedLabel = options.find((opt) => opt.value === selectedValue)?.label || label;

	return (
		<View style={styles.dropdownContainer}>
			<Text style={styles.formLabel}>{label}</Text>
			<TouchableOpacity style={styles.dropdownHeader} onPress={() => setIsOpen(!isOpen)}>
				<Text style={styles.dropdownSelectedValue}>{selectedLabel}</Text>
				<FontAwesome6 name={isOpen ? "chevron-up" : "chevron-down"} size={16} color="#ccc" />
			</TouchableOpacity>
			{isOpen && (
				<View style={styles.dropdownOptions}>
					{options.map((option) => (
						<TouchableOpacity
							key={option.value}
							style={styles.dropdownOption}
							onPress={() => {
								onSelect(option.value);
								setIsOpen(false);
							}}>
							<Text style={styles.dropdownOptionText}>{option.label}</Text>
						</TouchableOpacity>
					))}
				</View>
			)}
		</View>
	);
};

// Simple Segmented Control
const SegmentedControl = ({ label, options, selectedValue, onSelect }) => (
	<View style={styles.formGroup}>
		<Text style={styles.formLabel}>{label}</Text>
		<View style={styles.segmentedControlContainer}>
			{options.map((option) => (
				<TouchableOpacity key={option.value} style={[styles.segmentOption, selectedValue === option.value && styles.segmentOptionSelected]} onPress={() => onSelect(option.value)}>
					<Text style={[styles.segmentText, selectedValue === option.value && styles.segmentTextSelected]}>{option.label}</Text>
				</TouchableOpacity>
			))}
		</View>
	</View>
);

// Define available data sources outside the component for clarity
const availableDataSources = [
	{
		id: "user_app_streak",
		label: "App Streak",
		category: "User Account",
		description: "Tracks streaks based on how often users open the app",
	},
	// Add more data sources here
];

// Extract unique categories
const dataSourceCategories = ["All", ...new Set(availableDataSources.map((ds) => ds.category))];

const ComponentEditorControls = ({ config, dispatch, onOpenIconPicker }) => {
	// State for the selected category
	const [selectedCategory, setSelectedCategory] = useState("All");

	const updateConfig = (key, value) => {
		dispatch({ type: "UPDATE_CONFIG", payload: { [key]: value } });
	};

	const updateDataConfig = (key, value) => {
		dispatch({ type: "UPDATE_DATA_CONFIG", payload: { [key]: value } });
	};

	const handleDataTypeChange = (value) => {
		dispatch({ type: "CHANGE_DATA_TYPE", payload: value });
	};

	const renderDataTypeSpecificControls = () => {
		switch (config.dataType) {
			case "progressbar":
				return (
					<>
						<View style={styles.switchGroup}>
							<Text style={styles.formLabel}>User Selectable Max Value?</Text>
							<Switch
								trackColor={{ false: "#767577", true: "#81b0ff" }}
								thumbColor={config.dataConfig.dynamicMaxValue ? "#0E98FF" : "#f4f3f4"}
								ios_backgroundColor="#3e3e3e"
								onValueChange={(value) => updateDataConfig("dynamicMaxValue", value)}
								value={config.dataConfig.dynamicMaxValue || false}
							/>
						</View>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Max Value</Text>
							<TextInput
								style={[styles.formInput, config.dataConfig.dynamicMaxValue && styles.disabledInput]}
								value={config.dataConfig.maxValue?.toString() || ""}
								onChangeText={(text) => updateDataConfig("maxValue", parseInt(text) || 0)}
								keyboardType="numeric"
								placeholder={config.dataConfig.dynamicMaxValue ? "Auto (Dynamic)" : "e.g., 100"}
								placeholderTextColor="#666"
								editable={!config.dataConfig.dynamicMaxValue}
							/>
						</View>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Unit Label</Text>
							<TextInput style={styles.formInput} value={config.dataConfig.unit || ""} onChangeText={(text) => updateDataConfig("unit", text)} placeholder="e.g., Books Read" placeholderTextColor="#666" />
						</View>
						<View style={styles.switchGroup}>
							<Text style={styles.formLabel}>Show Percentage?</Text>
							<Switch
								trackColor={{ false: "#767577", true: "#81b0ff" }}
								thumbColor={config.dataConfig.showPercentage ? "#0E98FF" : "#f4f3f4"}
								ios_backgroundColor="#3e3e3e"
								onValueChange={(value) => updateDataConfig("showPercentage", value)}
								value={config.dataConfig.showPercentage || false}
							/>
						</View>
					</>
				);
			case "value":
				return (
					<>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Unit Label</Text>
							<TextInput style={styles.formInput} value={config.dataConfig.unit || ""} onChangeText={(text) => updateDataConfig("unit", text)} placeholder="e.g., Books Read" placeholderTextColor="#666" />
						</View>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Prefix</Text>
							<TextInput style={styles.formInput} value={config.dataConfig.prefix || ""} onChangeText={(text) => updateDataConfig("prefix", text)} placeholder="e.g., $" placeholderTextColor="#666" />
						</View>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Suffix</Text>
							<TextInput style={styles.formInput} value={config.dataConfig.suffix || ""} onChangeText={(text) => updateDataConfig("suffix", text)} placeholder="e.g., kg" placeholderTextColor="#666" />
						</View>
					</>
				);
			case "icon":
				const currentIconName = config.dataConfig?.iconName;
				return (
					<>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Icon</Text>
							<TouchableOpacity style={styles.iconPickerButton} onPress={onOpenIconPicker}>
								{currentIconName ? (
									<View style={styles.iconPreviewContainer}>
										<FontAwesome6 name={currentIconName} size={20} color="#fff" />
										<Text style={styles.iconPickerButtonText}>{currentIconName}</Text>
									</View>
								) : (
									<Text style={styles.iconPickerButtonText}>Select Icon...</Text>
								)}
								<FontAwesome6 name="chevron-right" size={16} color="#ccc" />
							</TouchableOpacity>
						</View>
						<View style={styles.formGroup}>
							<Text style={styles.formLabel}>Text</Text>
							<TextInput style={styles.formInput} value={config.dataConfig.text || ""} onChangeText={(text) => updateDataConfig("text", text)} placeholder="e.g., days" placeholderTextColor="#666" />
						</View>
					</>
				);
			// Add 'datasource' case later
			default:
				return null;
		}
	};

	return (
		<View style={styles.controlsContainer}>
			<Text style={styles.controlsTitle}>Component Configuration</Text>

			<View style={styles.formGroup}>
				<Text style={styles.formLabel}>Header Text</Text>
				<TextInput style={styles.formInput} value={config.header || ""} onChangeText={(text) => updateConfig("header", text)} placeholder="Component Title" placeholderTextColor="#666" />
			</View>

			<SegmentedControl
				label="Size"
				options={[
					{ label: "Half", value: "half" },
					{ label: "Full", value: "full" },
				]}
				selectedValue={config.size || "half"}
				onSelect={(value) => updateConfig("size", value)}
			/>

			<SimpleDropdown
				label="Data Type"
				options={[
					{ label: "Value + Unit", value: "value" },
					{ label: "Progress Bar", value: "progressbar" },
					{ label: "Icon + Text", value: "icon" },
					// { label: 'Data Source', value: 'datasource' }, // Future option
				]}
				selectedValue={config.dataType}
				onSelect={handleDataTypeChange}
			/>

			{/* --- Data Source Category Filter --- */}
			<SimpleDropdown
				label="Live Source Category"
				options={dataSourceCategories.map((cat) => ({ label: cat, value: cat }))}
				selectedValue={selectedCategory}
				onSelect={(category) => {
					setSelectedCategory(category);
					// Reset data source if category changes? Optional, but might be good UX
					if (config.dataSource && config.dataSource.category !== category && category !== "All") {
						updateConfig("dataSource", null);
					}
				}}
			/>

			{/* --- Filtered Data Source Selector --- */}
			<SimpleDropdown
				label="Live Source"
				options={[
					{ label: "None", value: "None" },
					...availableDataSources.filter((ds) => selectedCategory === "All" || ds.category === selectedCategory).map((ds) => ({ label: ds.label, value: ds.id })), // Pass only id as value
				]}
				selectedValue={config.dataSource?.id || "None"}
				onSelect={(value) => {
					const selectedOption = availableDataSources.find((ds) => ds.id === value);
					updateConfig(
						"dataSource",
						selectedOption
							? {
									id: selectedOption.id,
									label: selectedOption.label,
									description: selectedOption.description,
									category: selectedOption.category, // Store category too
							  }
							: null // Set to null if 'None' is selected
					);
				}}
			/>
			{/* Display description if a data source is selected */}
			{config.dataSource?.description && (
				<View style={styles.descriptionContainer}>
					<Text style={styles.descriptionText}>{config.dataSource.description}</Text>
				</View>
			)}
			{/* --- End Data Source Selection --- */}

			{renderDataTypeSpecificControls()}

			{/* Add more general controls later (e.g., background, border) */}
		</View>
	);
};

const styles = StyleSheet.create({
	controlsContainer: {
		padding: 16,
	},
	controlsTitle: {
		color: "#fff",
		fontSize: 16,
		fontWeight: "600",
		marginBottom: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#444",
		paddingBottom: 10,
	},
	formGroup: {
		marginBottom: 16,
	},
	switchGroup: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
		paddingVertical: 8, // Add padding for better spacing
	},
	formLabel: {
		color: "#ccc",
		fontSize: 13,
		marginBottom: 8,
		fontWeight: "500",
	},
	formInput: {
		backgroundColor: "#333",
		color: "#fff",
		borderRadius: 4,
		paddingHorizontal: 12,
		paddingVertical: 10,
		fontSize: 14,
	},
	// --- Segmented Control Styles ---
	segmentedControlContainer: {
		flexDirection: "row",
		backgroundColor: "#333",
		borderRadius: 8,
		overflow: "hidden",
		borderWidth: 1,
		borderColor: "#444",
	},
	segmentOption: {
		flex: 1,
		paddingVertical: 10,
		alignItems: "center",
		justifyContent: "center",
	},
	segmentOptionSelected: {
		backgroundColor: "#0E98FF", // Selection color
	},
	segmentText: {
		color: "#ccc",
		fontSize: 13,
	},
	segmentTextSelected: {
		color: "#fff",
		fontWeight: "600",
	},
	// --- Dropdown Styles ---
	dropdownContainer: {
		marginBottom: 16,
	},
	dropdownHeader: {
		backgroundColor: "#333",
		borderRadius: 4,
		paddingHorizontal: 12,
		paddingVertical: 10,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	dropdownSelectedValue: {
		color: "#fff",
		fontSize: 14,
	},
	dropdownOptions: {
		backgroundColor: "#333",
		borderRadius: 4,
		marginTop: 4,
		borderColor: "#555",
		borderWidth: 1,
		maxHeight: 150, // Limit height
	},
	dropdownOption: {
		paddingHorizontal: 12,
		paddingVertical: 10,
		borderBottomWidth: 1,
		borderBottomColor: "#444",
	},
	dropdownOptionText: {
		color: "#fff",
		fontSize: 14,
	},
	// Remove border from last option
	dropdownOption_lastChild: {
		borderBottomWidth: 0,
	},
	// --- Description Text Style ---
	descriptionContainer: {
		marginTop: -8, // Adjust spacing based on layout
		marginBottom: 16,
		paddingHorizontal: 12,
		paddingVertical: 8,
		backgroundColor: "#2a2a2a", // Slightly different background
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "#444",
	},
	descriptionText: {
		color: "#bbb",
		fontSize: 12,
		fontStyle: "italic",
	},
	// --- Icon Picker Button Style ---
	iconPickerButton: {
		backgroundColor: "#333",
		borderRadius: 4,
		paddingHorizontal: 12,
		paddingVertical: 10,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	iconPreviewContainer: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	iconPickerButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	disabledInput: {
		backgroundColor: "#272727",
		color: "#999",
	},
});

export default ComponentEditorControls;
